import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import { useMarqueeSelection } from '@/features/tasks/hooks/useMarqueeSelection';
import { useCardContextMenu } from '@/features/tasks/hooks/useCardContextMenu';
import { selectKeyRange } from '@/features/tasks/utils/cardSelectionClick';
import { ProjectCard } from './ProjectCard';
import { UsecasesBulkActionsBar } from './UsecasesBulkActionsBar.jsx';
import './GroupedFlatTableView.css';

/**
 * All-projects cards grid with drag-to-select. Move is task-only, so the
 * bulk bar here is Delete and sits next to the "All Projects" title while a
 * selection is active. Figma Projects `8007:4572` / marquee `8001:3358`.
 */
export const AllProjectsView = ({
    groupedProjects,
    allApps,
    onOpenProject,
    onEditProject,
    onDeleteProject,
}) => {
    const [selectedKeys, setSelectedKeys] = useState([]);
    const selectedKeysRef = useRef([]);
    const snapshotKeysRef = useRef(null);
    const anchorKeyRef = useRef(null);
    const gridRef = useRef(null);
    selectedKeysRef.current = selectedKeys;
    const { role } = useAuth();
    const isViewer = role === 'Viewer';

    const allProjectKeys = useMemo(
        () => groupedProjects.map((app) => app.group_name),
        [groupedProjects]
    );

    useEffect(() => {
        setSelectedKeys((prev) => prev.filter((key) => allProjectKeys.includes(key)));
    }, [allProjectKeys]);

    const applyMarqueeIds = useCallback((ids, { additive = false, preview = false } = {}) => {
        if (preview) {
            if (snapshotKeysRef.current == null) {
                snapshotKeysRef.current = additive ? selectedKeysRef.current : [];
            }
            setSelectedKeys(
                additive ? [...new Set([...snapshotKeysRef.current, ...ids])] : ids
            );
            return;
        }
        const base = additive ? (snapshotKeysRef.current || selectedKeysRef.current) : [];
        const next = additive ? [...new Set([...base, ...ids])] : ids;
        setSelectedKeys(next);
        snapshotKeysRef.current = null;
        if (ids.length > 0) {
            anchorKeyRef.current = ids[ids.length - 1];
        }
    }, []);

    const { marquee, isDragging, wasDrag } = useMarqueeSelection({
        containerRef: gridRef,
        enabled: !isViewer,
        itemAttr: 'data-project-key',
        onSelect: applyMarqueeIds,
    });

    const handleSelectAll = useCallback(() => {
        setSelectedKeys(allProjectKeys);
    }, [allProjectKeys]);

    const handleDeselectAll = useCallback(() => {
        setSelectedKeys([]);
    }, []);

    const handleToggleSelect = useCallback((key) => {
        anchorKeyRef.current = key;
        setSelectedKeys((prev) => (
            prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
        ));
    }, []);

    const handleRangeSelect = useCallback((key) => {
        setSelectedKeys(selectKeyRange(allProjectKeys, anchorKeyRef.current, key));
    }, [allProjectKeys]);

    const { contextMenu, closeContextMenu, onCardContextMenu } = useCardContextMenu({
        selectedCount: selectedKeys.length,
        setSelectedKeys,
        setAnchorKey: (key) => { anchorKeyRef.current = key; },
    });

    const handleBulkDelete = useCallback(async () => {
        const selected = groupedProjects.filter((app) => selectedKeys.includes(app.group_name));
        let anySuccess = false;
        for (const app of selected) {
            const result = await onDeleteProject?.(app);
            if (result?.success) anySuccess = true;
        }
        if (anySuccess) setSelectedKeys([]);
    }, [groupedProjects, onDeleteProject, selectedKeys]);

    const selectionActive = !isViewer && selectedKeys.length > 0;

    return (
        <div className="home-page-cards-view">
            <div className="home-page-cards-heading">
                <div className="home-page-cards-heading__lead">
                    <div className="fs-unmask home-page-cards-heading__title">
                        All Projects
                    </div>
                    {selectionActive ? (
                        <UsecasesBulkActionsBar
                            variant="inline"
                            selectedCount={selectedKeys.length}
                            totalCount={allProjectKeys.length}
                            onSelectAll={handleSelectAll}
                            onDeselectAll={handleDeselectAll}
                            onDelete={handleBulkDelete}
                            entityNoun="project"
                            itemNames={selectedKeys}
                            contextMenu={contextMenu}
                            onContextMenuClose={closeContextMenu}
                        />
                    ) : null}
                </div>
            </div>
            <div
                ref={gridRef}
                className={isDragging ? 'home-page-marquee-surface is-dragging' : 'home-page-marquee-surface'}
            >
                <div className="home-page-cards-grid">
                {groupedProjects.map((app) => (
                    <div
                        key={app.group_name}
                        className="home-page-card-cell"
                        data-project-key={app.group_name}
                        onContextMenu={isViewer ? undefined : (event) => onCardContextMenu(
                            event,
                            app.group_name,
                            () => handleToggleSelect(app.group_name),
                        )}
                    >
                        <ProjectCard
                            app={app}
                            allApps={allApps}
                            selected={selectedKeys.includes(app.group_name)}
                            selectionKey={app.group_name}
                            onToggleSelect={isViewer ? undefined : () => handleToggleSelect(app.group_name)}
                            onRangeSelect={isViewer ? undefined : () => handleRangeSelect(app.group_name)}
                            shouldSuppressClick={wasDrag}
                            onOpen={onOpenProject}
                            onEdit={onEditProject}
                            onDelete={onDeleteProject}
                        />
                    </div>
                ))}
                </div>
                {marquee ? (
                    <div
                        className="home-page-marquee"
                        style={{
                            left: marquee.left,
                            top: marquee.top,
                            width: marquee.width,
                            height: marquee.height,
                        }}
                    />
                ) : null}
            </div>
        </div>
    );
};

export default AllProjectsView;
